import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext.jsx";
import { Heart, ClipboardCheck, Search, GraduationCap, Plane, HeartHandshake, Briefcase, Coins, BookOpen, Users, Dumbbell, X, Sparkles, Check, Award, Rocket, Smile, Globe2, MessageCircle, BadgeCheck } from "lucide-react";
import SEO, { SITE_URL } from "./SEO.jsx";
import { pickDeadlineSoonPrograms, getCountdownParts, formatDeadlineDate, formatDeadlineDateShort } from "../utils/deadline";
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { MARKET } from '../utils/market.js';
import "./HomePage.css";

const API_BASE = process.env.REACT_APP_API_URL || `${GREENWICH_BACKEND_URL}/api`;
const BACKEND_URL = `${GREENWICH_BACKEND_URL}`;

function fixImageUrl(...urls) {
  for (const url of urls) {
    if (!url || url === 'null' || url === 'undefined' || String(url).trim() === '') continue;
    const s = String(url).trim();
    if (s.startsWith('blob:') || s.startsWith('http')) return s;
    if (s.startsWith('/')) return `${BACKEND_URL}${s}`;
    return s;
  }
  return '';
}

// NOTE: the old ComingSoonOverlay component (Partners / Member Feedback /
// Supported By placeholder blocks) and the Top Organizations podium (which
// only ever rendered hardcoded "Unknown" placeholder data) were removed from
// the homepage render below — see the comment near the bottom of this file
// for why.

// Deterministic pseudo-random int in [min,max] seeded from a string, so a
// card's like/comment counts stay stable across re-renders instead of
// reshuffling every animation frame.
function seededInt(seed, min, max) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return min + (h % (max - min + 1));
}

/* ── Draggable, self-scrolling strip of country cards (fed from the DB, not the bundle).
   Cards shrink near the center and grow + fade out near the edges, like a warped carousel.
   Styled as feed-post cards (avatar, @erika, likes/comments) so they read as
   real trip posts from the Greenwich community rather than plain stock photos. */
function CountryHighlightsStrip() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const cardRefs = useRef([]);
  const cardMotion = useRef([]);
  const dragState = useRef({ x: 0, dragging: false, startClientX: 0, startX: 0, pointerId: null });

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/country-highlights`)
      .then(res => res.json())
      .then(data => { if (!cancelled && data?.success) setItems(data.items || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const track = useMemo(() => [...items, ...items], [items]);

  useEffect(() => {
    if (items.length === 0) return;
    const SPEED = 16; // px per second — slow, deliberate drift
    let raf;
    let lastTime = performance.now();

    const step = (now) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;
      const st = dragState.current;
      const trackEl = trackRef.current;
      const containerEl = containerRef.current;
      if (!trackEl || !containerEl) { raf = requestAnimationFrame(step); return; }

      if (!st.dragging) st.x -= SPEED * dt;

      const singleSetWidth = trackEl.scrollWidth / 2;
      if (singleSetWidth > 0) {
        // Wrap with modulo (not a single conditional subtract) so a fast drag
        // in either direction can never land outside the rendered track and
        // cause a visible jump/blank flash.
        let wrapped = st.x % singleSetWidth;
        if (wrapped > 0) wrapped -= singleSetWidth;
        st.x = wrapped;
      }
      trackEl.style.transform = `translateX(${st.x}px)`;

      const containerRect = containerEl.getBoundingClientRect();
      const centerX = containerRect.left + containerRect.width / 2;
      const half = containerRect.width / 2;
      const ease = 1 - Math.pow(0.001, dt); // framerate-independent lerp factor
      cardRefs.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const cardCenter = r.left + r.width / 2;
        const signedT = Math.max(-1, Math.min((cardCenter - centerX) / half, 1));
        const t = Math.abs(signedT);
        const targetScale = 1.12 - t * 0.4;
        const fadeStart = 0.7;
        const targetOpacity = t <= fadeStart ? 1 : Math.max(0, 1 - (t - fadeStart) / (1 - fadeStart));
        const targetRotate = signedT * -14;
        const targetLift = -Math.sin(t * Math.PI * 0.5) * 10 + t * 14;

        const m = cardMotion.current[i] || (cardMotion.current[i] = { scale: targetScale, opacity: targetOpacity, rotate: targetRotate, lift: targetLift });
        m.scale += (targetScale - m.scale) * ease;
        m.opacity += (targetOpacity - m.opacity) * ease;
        m.rotate += (targetRotate - m.rotate) * ease;
        m.lift += (targetLift - m.lift) * ease;

        el.style.transform = `perspective(900px) translateY(${m.lift.toFixed(2)}px) rotateY(${m.rotate.toFixed(2)}deg) scale(${m.scale.toFixed(3)})`;
        el.style.opacity = m.opacity.toFixed(3);
      });

      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [items, track.length]);

  if (items.length === 0) return null;

  const onPointerDown = (e) => {
    const st = dragState.current;
    st.dragging = true;
    st.startClientX = e.clientX;
    st.startX = st.x;
    st.pointerId = e.pointerId;
    containerRef.current?.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const st = dragState.current;
    if (!st.dragging) return;
    st.x = st.startX + (e.clientX - st.startClientX);
  };
  const endDrag = () => { dragState.current.dragging = false; };

  return (
    <>
      <section className="hp-section-head">
        <h2 className="hp-section-title">{t("pages.home.countriesHeading")}</h2>
      </section>
      <section
        className="hp-countries"
        ref={containerRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onPointerCancel={endDrag}
      >
        <div className="hp-countries-track" ref={trackRef}>
          {track.map((item, i) => {
            const seed = `${item._id || item.country}`;
            const likes = seededInt(seed + 'l', 480, 4200);
            const comments = seededInt(seed + 'c', 6, 94);
            return (
              <div
                className="hp-country-card"
                key={`${item._id}-${i}`}
                ref={(el) => { cardRefs.current[i] = el; }}
              >
                <img className="hp-country-card-photo" src={item.imageUrl} alt={item.country} loading="lazy" draggable={false} />
                <div className="hp-country-card-scrim" aria-hidden="true" />
                <div className="hp-country-card-header">
                  <img src="/nkarner/ai-mascot.png" alt="" className="hp-country-card-avatar" draggable={false} />
                  <span className="hp-country-card-user">erika <BadgeCheck size={11} strokeWidth={2.6} /></span>
                </div>
                <div className="hp-country-card-footer">
                  <span className="hp-country-card-stat"><Heart size={13} strokeWidth={2.4} fill="currentColor" /> {likes.toLocaleString()}</span>
                  <span className="hp-country-card-stat"><MessageCircle size={13} strokeWidth={2.4} /> {comments}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}

/* ── A single draggable/clickable category bubble: drag it around, or click
   (without dragging) to pop open a card about that opportunity type. ── */
function CategoryBubble({ category, onOpen }) {
  const ref = useRef(null);
  const state = useRef({ dragging: false, moved: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  const [drag, setDrag] = useState({ x: 0, y: 0 });

  const onPointerDown = (e) => {
    const st = state.current;
    st.dragging = true;
    st.moved = false;
    st.startX = e.clientX;
    st.startY = e.clientY;
    st.baseX = drag.x;
    st.baseY = drag.y;
    ref.current?.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const st = state.current;
    if (!st.dragging) return;
    const dx = e.clientX - st.startX;
    const dy = e.clientY - st.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) st.moved = true;
    setDrag({ x: st.baseX + dx, y: st.baseY + dy });
  };
  const onPointerUp = () => {
    const st = state.current;
    st.dragging = false;
    if (!st.moved) onOpen();
  };

  const { Icon, label, top, left, delay, color } = category;
  return (
    <div
      ref={ref}
      className="hp-bubble"
      style={{ top, left, transform: `translate(${drag.x}px, ${drag.y}px)` }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <span className="hp-bubble-inner" style={{ animationDelay: delay }}>
        <span className="hp-bubble-icon" style={{ background: color }}>
          <Icon size={16} strokeWidth={2.4} />
        </span>
        <span>{label}</span>
      </span>
    </div>
  );
}

/* ── Search Opportunities CTA: a scattered field of draggable category
   bubbles — tap one (without dragging) to see what that opportunity offers. */
function CategoryOpportunitiesCta() {
  const { t } = useTranslation();
  const [openCategory, setOpenCategory] = useState(null);

  const categories = [
    { id: "scholarship",  label: t("pages.home.filterScholarship"),  Icon: GraduationCap,  benefit: t("pages.home.benefitScholarship"),  top: "4%",  left: "2%",  delay: "0s",    color: "#2E7A82" },
    { id: "erasmusplus",  label: t("pages.home.filterExchange"),     Icon: Plane,          benefit: t("pages.home.benefitExchange"),     top: "58%", left: "0%",  delay: "-2s",   color: "#3D6B78" },
    { id: "volunteering", label: t("pages.home.filterVolunteering"), Icon: HeartHandshake, benefit: t("pages.home.benefitVolunteering"), top: "8%",  left: "27%", delay: "-4s",   color: "#2E6B52" },
    { id: "internship",   label: t("pages.home.filterInternship"),   Icon: Briefcase,      benefit: t("pages.home.benefitInternship"),   top: "62%", left: "28%", delay: "-1s",   color: "#8F6A46" },
    { id: "grant",        label: t("pages.home.filterGrant"),        Icon: Coins,          benefit: t("pages.home.benefitGrant"),        top: "2%",  left: "54%", delay: "-3s",   color: "#A6763A" },
    { id: "education",    label: t("pages.home.filterEducation"),    Icon: BookOpen,       benefit: t("pages.home.benefitEducation"),    top: "58%", left: "54%", delay: "-5s",   color: "#4F8F6A" },
    { id: "fellowship",   label: t("pages.home.filterFellowship"),   Icon: Users,          benefit: t("pages.home.benefitFellowship"),   top: "10%", left: "78%", delay: "-2.5s", color: "#D99A3D" },
    { id: "training",     label: t("pages.home.filterTraining"),     Icon: Dumbbell,       benefit: t("pages.home.benefitTraining"),     top: "60%", left: "80%", delay: "-0.5s", color: "#C97A2E" },
  ];

  const active = categories.find((c) => c.id === openCategory);

  return (
    <>
      <div className="hp-bubble-field">
        {categories.map((c) => (
          <CategoryBubble key={c.id} category={c} onOpen={() => setOpenCategory(c.id)} />
        ))}
      </div>

      {active && createPortal(
        <div className="hp-bubble-popup-overlay" onClick={() => setOpenCategory(null)}>
          <div className="hp-bubble-popup" onClick={(e) => e.stopPropagation()}>
            <button className="hp-bubble-popup-close" onClick={() => setOpenCategory(null)} aria-label="Close">
              <X size={18} />
            </button>
            <div className="hp-bubble-popup-icon" style={{ background: `linear-gradient(135deg, ${active.color}, var(--green-900))` }}>
              <active.Icon size={26} strokeWidth={2.2} />
            </div>
            <h3 className="hp-bubble-popup-title">{active.label}</h3>
            <p className="hp-bubble-popup-text">{active.benefit}</p>
            <Link to={`/programs?category=${active.id}`} className="hp-search-cta-btn">
              <Search size={16} strokeWidth={2.4} />
              {t("pages.home.searchOpportunities")}
            </Link>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

/* ── Live countdown to a program deadline ── */
function DeadlineCountdown({ deadline, daysLabel }) {
  const [parts, setParts] = useState(() => getCountdownParts(deadline));

  useEffect(() => {
    const id = setInterval(() => setParts(getCountdownParts(deadline)), 1000);
    return () => clearInterval(id);
  }, [deadline]);

  const pad = n => String(n).padStart(2, '0');
  return (
    <span className="hp-countdown">
      {parts.days > 0 && <span className="hp-countdown-days">{parts.days}{daysLabel}</span>}
      <span className="hp-countdown-clock">{pad(parts.hours)}:{pad(parts.minutes)}:{pad(parts.seconds)}</span>
    </span>
  );
}

/* ── A number that counts up from 0 the first time it scrolls into view ── */
function CountUp({ target, suffix = '', locale, duration = 2800 }) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);
  // Several CountUps run at once on this page (hp-stats row) — re-rendering
  // on every animation frame regardless of whether the rounded number
  // actually changed was visibly janky on lower-end phones. Only setState
  // when the displayed value would really change.
  const lastValueRef = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const run = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const start = performance.now();
      const ease = t => 1 - Math.pow(1 - t, 3);
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const next = Math.round(target * ease(t));
        if (next !== lastValueRef.current) {
          lastValueRef.current = next;
          setValue(next);
        }
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      run();
    }, { threshold: 0.4 });
    observer.observe(el);

    // Fallback: some browsers/layouts (e.g. the element is already in the
    // viewport at mount with zero intermediate layout change, or an
    // IntersectionObserver edge case) never fire the callback, leaving the
    // number stuck at 0 forever. If that hasn't happened shortly after
    // mount, just start the animation anyway — worst case it plays slightly
    // early instead of not playing at all.
    const fallback = setTimeout(run, 1200);

    return () => { observer.disconnect(); clearTimeout(fallback); };
  }, [target, duration]);

  return <span ref={ref} className="hp-trust-number">{value.toLocaleString(locale)}{suffix}</span>;
}

/* ── Fades/slides its content in the first time it scrolls into view ── */
function Reveal({ children, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setVisible(true);
      observer.disconnect();
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`hp-reveal${visible ? ' is-in' : ''}${className ? ` ${className}` : ''}`}>
      {children}
    </div>
  );
}

function HomePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = Boolean(user);
  const [hotPrograms, setHotPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440
  );

  const [openFaqs, setOpenFaqs] = useState(() => new Set([1]));
  const toggleFaq = (n) => setOpenFaqs(prev => {
    const next = new Set(prev);
    next.has(n) ? next.delete(n) : next.add(n);
    return next;
  });

  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ name: '', email: '', message: '', rating: 0 });
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;
  const isMediumDesktop = windowWidth > 1024 && windowWidth <= 1280;

  const hotProgramsMaxCols = isTablet ? 2 : isMediumDesktop ? 3 : 6;
  // Size the grid to how many cards there actually are, so a short list
  // (e.g. only 2 programs with a deadline today/tomorrow) doesn't leave
  // a lopsided empty gap on the right of a fixed 6-column grid.
  const hotProgramsCols = Math.max(1, Math.min(hotProgramsMaxCols, hotPrograms.length || hotProgramsMaxCols));
  const hotProgramsColumns = isMobile ? '1fr' : `repeat(${hotProgramsCols}, 1fr)`;

  useEffect(() => {
    const loadHotPrograms = async () => {
      setLoading(true);
      try {
        try {
          const token = localStorage.getItem("token");
          const res = await fetch(`${API_BASE}/programs/approved?market=${MARKET}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
          if (res.ok) {
            const data = await res.json();
            const list = data.programs || data;
            if (Array.isArray(list) && list.length > 0) {
              setHotPrograms(pickDeadlineSoonPrograms(list));
              return;
            }
          }
        } catch (e) { console.warn(e); }

        const saved = localStorage.getItem('admin_programs');
        if (saved) {
          const allPrograms = JSON.parse(saved);
          const approvedActive = allPrograms.filter(p =>
            (p.approval_status === 'approved' || p.status === 'approved') && p.is_active !== false
          );
          setHotPrograms(pickDeadlineSoonPrograms(approvedActive));
          return;
        }

        try {
          const res = await fetch('/scraper_project (1)/scraper_project/programs_data.json');
          const data = await res.json();
          const approved = data.filter(p => p.approval_status === 'approved' && p.is_active === true);
          setHotPrograms(pickDeadlineSoonPrograms(approved));
        } catch (e) { setHotPrograms([]); }
      } catch (error) {
        setHotPrograms([]);
      } finally {
        setLoading(false);
      }
    };

    loadHotPrograms();
    const handleStorageChange = (e) => {
      if (e.key === 'admin_programs' || !e.key) loadHotPrograms();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleFeedbackSubmit = async () => {
    if (!feedbackForm.message.trim()) return;
    setFeedbackLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/beta-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...feedbackForm, page: window.location.pathname }),
      });
      if (res.ok) {
        setFeedbackSent(true);
        setTimeout(() => {
          setShowFeedbackPopup(false);
          setFeedbackSent(false);
          setFeedbackForm({ name: '', email: '', message: '', rating: 0 });
        }, 2500);
      }
    } catch (e) { console.error(e); }
    setFeedbackLoading(false);
  };

  const closeFeedbackPopup = () => {
    setShowFeedbackPopup(false);
    setFeedbackSent(false);
    setFeedbackForm({ name: '', email: '', message: '', rating: 0 });
  };

  return (
    <div className="home-page">
      <SEO
        titleKey="pages.seo.home.title"
        descriptionKey="pages.seo.home.description"
        keywordsKey="pages.seo.home.keywords"
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Greenwich",
            alternateName: ["Greenwich Armenia", "Greenwich.am", "Greenwitch", "Grinvich", "Гринвич", "Гринвич Армения", "Գրինվիչ", "Գրինվիչ Արմենիա"],
            url: SITE_URL,
            potentialAction: {
              "@type": "SearchAction",
              target: `${SITE_URL}/programs?q={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [1, 2, 3, 4, 5, 6].map((n) => ({
              "@type": "Question",
              name: t(`pages.home.faqQ${n}`),
              acceptedAnswer: { "@type": "Answer", text: t(`pages.home.faqA${n}`) },
            })),
          },
        ]}
      />
      {/* ── Hero Section ── */}
      <section className="hp-hero">
        <div className="hp-hero-media" style={{ backgroundImage: "url('/nkarner/hero-main.jpg')" }} />
        <div className="hp-hero-scrim" />
        <div className="hp-hero-content">
          <h1 className="hp-hero-title">{t("pages.home.hero1")}<br />{t("pages.home.hero2")}</h1>
          {!isMobile && (
            <p className="hp-hero-sub">{t("pages.home.heroSubDesktop")}</p>
          )}
          {isMobile && (
            <p className="hp-hero-sub">{t("pages.home.heroSubMobile")}</p>
          )}
          <Link to="/programs" className="hp-btn-hero">
            {t("pages.home.explorePrograms")} <span className="hp-btn-hero-arrow">→</span>
          </Link>
        </div>
        <div className="hp-hero-fade" />
      </section>

      {/* ── Stats Showcase ── */}
      <section className="hp-stats">
        <span className="hp-stats-kicker"><Award size={14} strokeWidth={2.4} /> {t("pages.home.statsKicker")}</span>
        <div className="hp-stats-row">
          <div className="hp-stats-item">
            <span className="hp-stats-badge hp-stats-badge--a">
              <Users className="hp-stats-icon" size={32} strokeWidth={1.8} />
              <span className="hp-stats-number"><CountUp target={6000} suffix="+" locale={i18n.language} duration={2600} /></span>
            </span>
            <span className="hp-stats-label">{t("pages.home.trustLabelUsers")}</span>
          </div>
          <div className="hp-stats-item">
            <span className="hp-stats-badge hp-stats-badge--b">
              <Rocket className="hp-stats-icon" size={32} strokeWidth={1.8} />
              <span className="hp-stats-number"><CountUp target={4000} suffix="+" locale={i18n.language} duration={3100} /></span>
            </span>
            <span className="hp-stats-label">{t("pages.home.trustLabelOpportunities")}</span>
          </div>
          <div className="hp-stats-item">
            <span className="hp-stats-badge hp-stats-badge--c">
              <Smile className="hp-stats-icon" size={32} strokeWidth={1.8} />
              <span className="hp-stats-number"><CountUp target={87} suffix="%" locale={i18n.language} duration={2200} /></span>
            </span>
            <span className="hp-stats-label">{t("pages.home.trustLabelSatisfaction")}</span>
          </div>
          <div className="hp-stats-item">
            <span className="hp-stats-badge hp-stats-badge--d">
              <Globe2 className="hp-stats-icon" size={32} strokeWidth={1.8} />
              <span className="hp-stats-number"><CountUp target={75} suffix="+" locale={i18n.language} duration={2900} /></span>
            </span>
            <span className="hp-stats-label">{t("pages.home.trustLabelCountries")}</span>
          </div>
        </div>
      </section>

      {/* ── Roles Section (registration CTAs — only for guests) ── */}
      {!isLoggedIn && (
        <section className="hp-roles">
          <Link to="/PlayerReg" className="hp-role-card hp-role-card--player">
            <span className="hp-role-blob-3" />
            <span className="hp-role-card-body">
              <span className="hp-role-card-text">{t("pages.home.roleCardPlayer")}</span>
              <span className="hp-role-card-sub">{t("pages.home.roleCardPlayerSub")}</span>
            </span>
            <span className="hp-role-icon">
              <span className="hp-role-icon-ring" />
              <Heart className="hp-role-icon-glyph" strokeWidth={2.2} />
            </span>
          </Link>
          <Link to="/Reg" className="hp-role-card hp-role-card--organizer">
            <span className="hp-role-blob-3" />
            <span className="hp-role-card-body">
              <span className="hp-role-card-text">{t("pages.home.roleCardOrganizer")}</span>
              <span className="hp-role-card-sub">{t("pages.home.roleCardOrganizerSub")}</span>
            </span>
            <span className="hp-role-icon">
              <span className="hp-role-icon-ring" />
              <ClipboardCheck className="hp-role-icon-glyph" strokeWidth={2.2} />
            </span>
          </Link>
        </section>
      )}

      {/* ── Search Opportunities CTA ── */}
      <section className="hp-search-cta">
        <div className="hp-search-cta-stat">
          <span className="hp-search-cta-number">10,000,000+</span>
          <p className="hp-search-cta-text">{t("pages.home.searchCtaText")}</p>
          <p className="hp-search-cta-hint">{t("pages.home.searchCtaHint")}</p>
        </div>
        <div className="hp-search-cta-actions">
          <Link to="/programs" className="hp-search-cta-btn">
            <Search size={18} strokeWidth={2.4} />
            {t("pages.home.searchOpportunities")}
          </Link>
          <CategoryOpportunitiesCta />
        </div>
      </section>

      {/* ── Deadline Soon Header ── */}
      <section className="hp-section-head">
        <h2 className="hp-section-title">{t("pages.home.hotNow")}</h2>
        <Link to="/programs">
          <button className="hp-btn-ghost">{t("pages.home.seeMore")}</button>
        </Link>
      </section>

      {/* ── Deadline Soon Cards ── */}
      <section className="hp-hotnow">
        {loading ? (
          isMobile ? (
            <div className="hp-hotnow-scroll">
              {[1, 2, 3].map(i => <div key={i} className="hp-skel hp-skel--mobile" />)}
            </div>
          ) : (
            <div className="hp-hotnow-grid" style={{ gridTemplateColumns: hotProgramsColumns }}>
              {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="hp-skel hp-skel--desktop" />)}
            </div>
          )
        ) : hotPrograms.length > 0 ? (
          isMobile ? (
            <div className="hp-hotnow-scroll">
              {hotPrograms.map(({ program, deadline }, index) => {
                const imageUrl = fixImageUrl(program.image_url, program.imageUrl, program.image, program.photo, program.thumbnail);
                return (
                  <div key={program._id || program.id || index}
                    onClick={() => window.location.href = '/programs'}
                    className="hp-program-card hp-program-card--mobile">
                    <div className="hp-program-media">
                      {imageUrl ? (
                        <img src={imageUrl} alt={program.title || t('pages.home.programFallback')} onError={e => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="hp-program-placeholder" style={{ fontSize: '32px' }}>🌍</div>
                      )}
                      <div className="hp-badge-deadline">{t("pages.home.deadlineBadge")}</div>
                    </div>
                    <div className="hp-program-body">
                      <h3 className="hp-program-title">
                        {program.title || t('pages.home.untitledProgram')}
                      </h3>
                      <div className="hp-program-deadline-row">
                        <span className="hp-program-deadline-date" title={formatDeadlineDate(deadline, i18n.language)}>{formatDeadlineDateShort(deadline, i18n.language)}</span>
                        <DeadlineCountdown deadline={deadline} daysLabel={t("pages.home.daysShort")} />
                      </div>
                      <div className="hp-program-view">
                        <span>{t("pages.home.viewArrow")}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="hp-hotnow-grid" style={{ gridTemplateColumns: hotProgramsColumns }}>
              {hotPrograms.map(({ program, deadline }, index) => {
                const imageUrl = fixImageUrl(program.image_url, program.imageUrl, program.image, program.photo, program.thumbnail);
                return (
                  <div key={program._id || program.id || index}
                    onClick={() => window.location.href = '/programs'}
                    className="hp-program-card">
                    <div className="hp-program-media" style={{ height: '150px' }}>
                      {imageUrl ? (
                        <img src={imageUrl} alt={program.title || t('pages.home.programFallback')} onError={e => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="hp-program-placeholder" style={{ fontSize: '48px' }}>🌍</div>
                      )}
                      <div className="hp-badge-deadline">{t("pages.home.deadlineBadge")}</div>
                    </div>
                    <div className="hp-program-body">
                      <h3 className="hp-program-title">
                        {program.title || t('pages.home.untitledProgram')}
                      </h3>
                      <div className="hp-program-deadline-row">
                        <span className="hp-program-deadline-date" title={formatDeadlineDate(deadline, i18n.language)}>{formatDeadlineDateShort(deadline, i18n.language)}</span>
                        <DeadlineCountdown deadline={deadline} daysLabel={t("pages.home.daysShort")} />
                      </div>
                      <div className="hp-program-view">
                        <span>{t("pages.home.viewArrow")}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <div className="hp-empty-state">
            {t("pages.home.noDeadlinesSoon")}
          </div>
        )}
      </section>

      {/* ── AI Matching ── */}
      <section className="hp-ai">
        <Reveal className="hp-ai-inner">
          <img src="/nkarner/ai-mascot.png" alt="" aria-hidden="true" className="hp-ai-mascot" loading="lazy" />
          <div className="hp-ai-copy">
            <span className="hp-ai-kicker"><Sparkles size={13} strokeWidth={2.6} /> {t("pages.home.aiKicker")}</span>
            <h2 className="hp-ai-title">{t("pages.home.aiTitle")}</h2>
            <p className="hp-ai-desc">{t("pages.home.aiDesc")}</p>
            <div className="hp-ai-values">
              <p className="hp-ai-value"><Check size={20} strokeWidth={3} /> {t("pages.home.aiPoint1")}</p>
              <p className="hp-ai-value"><Check size={20} strokeWidth={3} /> {t("pages.home.aiPoint2")}</p>
              <p className="hp-ai-value"><Check size={20} strokeWidth={3} /> {t("pages.home.aiPoint3")}</p>
            </div>
            <Link to="/programs" className="hp-ai-cta">
              {t("pages.home.aiCta")}
              <Sparkles size={16} strokeWidth={2.4} />
            </Link>
          </div>
        </Reveal>
      </section>

      <CountryHighlightsStrip />

      {/* ── Buddy Mentorship ── */}
      <section className="hp-buddy">
        <Reveal className="hp-buddy-inner">
          <div className="hp-buddy-copy">
            <span className="hp-buddy-kicker"><HeartHandshake size={13} strokeWidth={2.6} /> {t("pages.home.buddyKicker")}</span>
            <h2 className="hp-buddy-title">{t("pages.home.buddyTitle")}</h2>
            <p className="hp-buddy-desc">{t("pages.home.buddyDesc")}</p>
            <div className="hp-buddy-values">
              <p className="hp-buddy-value"><Check size={20} strokeWidth={3} /> {t("pages.home.buddyPoint1")}</p>
              <p className="hp-buddy-value"><Check size={20} strokeWidth={3} /> {t("pages.home.buddyPoint2")}</p>
              <p className="hp-buddy-value"><Check size={20} strokeWidth={3} /> {t("pages.home.buddyPoint3")}</p>
            </div>
            <Link to="/buddy" className="hp-buddy-cta">
              {t("pages.home.buddyCta")}
              <HeartHandshake size={16} strokeWidth={2.4} />
            </Link>
          </div>
          <img src="/nkarner/buddy-mascot.png" alt="" aria-hidden="true" className="hp-buddy-mascot" loading="lazy" />
        </Reveal>
      </section>

      {/* ── FAQ (also feeds FAQPage structured data in SEO) ── */}
      <section className="hp-faq">
        <Reveal className="hp-faq-inner">
          <span className="hp-faq-kicker">{t("pages.home.faqKicker")}</span>
          <h2 className="hp-faq-title">{t("pages.home.faqTitle")}</h2>
          <div className="hp-faq-list">
            {[1, 2, 3, 4, 5, 6].map((n) => {
              const isOpen = openFaqs.has(n);
              return (
                <div className={`hp-faq-item${isOpen ? ' hp-faq-item-open' : ''}`} key={n}>
                  <button
                    type="button"
                    className="hp-faq-q"
                    aria-expanded={isOpen}
                    onClick={() => toggleFaq(n)}
                  >
                    <span>{t(`pages.home.faqQ${n}`)}</span>
                    <span className="hp-faq-toggle" aria-hidden="true">
                      <span className="hp-faq-toggle-bar hp-faq-toggle-bar-h" />
                      <span className="hp-faq-toggle-bar hp-faq-toggle-bar-v" />
                    </span>
                  </button>
                  <div className="hp-faq-a-wrap">
                    <div className="hp-faq-a-inner">
                      <p className="hp-faq-a">{t(`pages.home.faqA${n}`)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </section>

      {/*
        ── Top Organizations / Partners / Member Feedback / Supported By ──
        These sections previously rendered with fully hardcoded placeholder
        data (avatar images with no real org behind them and the name always
        resolving to the "Unknown"/"Unknown Organization" translation key)
        or a static "Coming soon" overlay. There is no real data source wired
        up for any of them yet, so instead of shipping a broken/placeholder
        state to real visitors, these are hidden until real content/data
        exists. See VerifiedOrganizations.jsx for the real, working org
        rankings (linked from /verified-organizations) — that page already
        has genuine data and does not need this teaser.
      */}

      {/* ── Beta Feedback Floating Button ── */}
      <button className="hp-fab" onClick={() => setShowFeedbackPopup(true)} title="Send Beta Feedback">💬</button>

      {/* ── Beta Feedback Popup ── */}
      {showFeedbackPopup && (
        <div onClick={closeFeedbackPopup} className="hp-popup-scrim">
          <div onClick={e => e.stopPropagation()} className="hp-popup-card">
            <button onClick={closeFeedbackPopup} className="hp-popup-close">✕</button>

            {feedbackSent ? (
              <div className="hp-popup-success">
                <div className="hp-popup-success-icon">🎉</div>
                <h3>{t("pages.home.thankYou")}</h3>
                <p>{t("pages.home.feedbackSent")}</p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 20 }}>
                  <span className="hp-popup-tag">{t("pages.home.betaVersion")}</span>
                  <h2 className="hp-popup-title">{t("pages.home.shareFeedback")}</h2>
                  <p className="hp-popup-sub">{t("pages.home.shareFeedbackSub")}</p>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div className="hp-rating-label">{t("pages.home.ratingOptional")}</div>
                  <div className="hp-rating-row">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button key={star}
                        onClick={() => setFeedbackForm(f => ({ ...f, rating: f.rating === star ? 0 : star }))}
                        className={`hp-star ${star <= feedbackForm.rating ? 'is-active' : ''}`}
                      >★</button>
                    ))}
                  </div>
                </div>
                {['name:Your name (optional):text', 'email:Email (optional):email'].map(f => {
                  const [key, placeholder, type] = f.split(':');
                  return (
                    <input key={key} placeholder={placeholder} type={type} value={feedbackForm[key]}
                      onChange={e => setFeedbackForm(p => ({ ...p, [key]: e.target.value }))}
                      className="hp-field"
                    />
                  );
                })}
                <textarea placeholder="Tell us what you think about this beta version..."
                  value={feedbackForm.message} onChange={e => setFeedbackForm(f => ({ ...f, message: e.target.value }))} rows={4}
                  className="hp-field"
                />
                <button onClick={handleFeedbackSubmit} disabled={feedbackLoading || !feedbackForm.message.trim()}
                  className={`hp-btn-submit ${feedbackForm.message.trim() ? 'is-ready' : ''}`}
                >
                  {feedbackLoading ? 'Sending...' : 'Send Feedback →'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default HomePage;
